import {
  Box,
  Button,
  Icon,
  InlineStack,
  Stack,
  Text,
  Tooltip,
  useSideNavState,
} from '@customerio/pluma-components/react';
import { useCallback } from 'react';
import { useStore } from '@nanostores/react';
import { usePermissions } from '@customerio/hydra-permissions/react';
import { useAuth } from '@/services/auth';
import { accountStore } from '@/services/auth/store';
import { useAnalytics } from '@/services/analytics';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { useSampleWorkspace } from '@/features/workspaces';
import { billing } from '@/services/billing';
import { isEmptyString } from '@/utils/string';
import {
  getTrialCtaDisplayData,
  shouldShowUpgradeCta as shouldShowUpgradeCtaStore,
  type TrialCtaDisplayData,
} from './trial-cta.utils';
import * as styles from './trial-cta.css';

type TrialCtaData = TrialCtaDisplayData & { handleUpgradeClick: () => void };

/** Subscribes to everything `getTrialCtaDisplayData` reads, so re-renders stay correct. */
function useTrialCtaInput() {
  const shouldShowUpgradeCta = useStore(shouldShowUpgradeCtaStore);
  const isPendingCheckout = useStore(billing.isPendingCheckout);
  const isUpgradeDisabled = useStore(billing.isUpgradeDisabled);
  const upgradeDisabledMessage = useStore(billing.upgradeDisabledMessage);
  const isOrdwayCustomer = useStore(billing.isOrdwayCustomer);
  const pinnedPlan = useStore(billing.pinnedPlan);

  // We want to subscribe to these stores, as if data changes here, the UI could be stale
  useStore(accountStore);
  useStore(billing.subscription);
  useStore(billing.billingInfo);

  const { hasPermission } = usePermissions();
  const { isSampleWorkspace } = useSampleWorkspace();
  const currentWorkspace = useCurrentWorkspace();
  const sideNavState = useSideNavState();

  const hasBillingViewPermission = hasPermission('account/billing/view');
  const isCollapsed = sideNavState?.isCollapsed ?? false;
  const isSampleWorkspaceValue = isSampleWorkspace(currentWorkspace?.id);
  const hasPinnedPlan = pinnedPlan != null;

  return {
    shouldShowUpgradeCta,
    daysLeftInTrial: billing.daysLeftInTrial,
    hasBillingViewPermission,
    hasPinnedPlan,
    isCollapsed,
    isOrdwayCustomer,
    isPendingCheckout,
    isSampleWorkspace: isSampleWorkspaceValue,
    isUpgradeDisabled,
    trialExpired: billing.trialExpired,
    trialExpiresAt: billing.trialExpiresAt,
    upgradeDisabledMessage,
  };
}

function useHandleUpgradeClick() {
  const { account } = useAuth();
  const { track } = useAnalytics();

  return useCallback(() => {
    void track('home', 'trialUpgradeClicked', { 'Account Id': account.id });
  }, [account.id, track]);
}

function useTrialCtaData(): TrialCtaData | null {
  const input = useTrialCtaInput();
  const handleUpgradeClick = useHandleUpgradeClick();

  if (!input.shouldShowUpgradeCta) {
    return null;
  }

  return { ...getTrialCtaDisplayData(input), handleUpgradeClick };
}

/** Trial "Upgrade" CTA + "N days left in trial" text shown at the bottom of the main nav. */
export function TrialCta() {
  const data = useTrialCtaData();

  if (data == null) {
    return null;
  }

  const {
    expiryDescription,
    handleUpgradeClick,
    isTooltipDisabled,
    isUpgradeDisabled,
    shouldShowUpgradeButton,
    tooltipContent,
    trialStatus,
    upgradeHref,
  } = data;

  return (
    <Stack alignInline="stretch" data-test-trial-cta>
      {shouldShowUpgradeButton ? (
        <Tooltip isCustom={true} content={tooltipContent} isDisabled={isTooltipDisabled} placement="right">
          {(tooltipContext) => (
            <Button
              {...tooltipContext.reference.getReferenceProps({ onClick: handleUpgradeClick })}
              ref={tooltipContext.reference.setReference}
              size="md"
              variant="primary"
              mt="100"
              href={!isUpgradeDisabled ? upgradeHref : undefined}
              isDisabled={isUpgradeDisabled}
              className={`${styles.trialCtaButton} ${styles.trialCtaButtonColor({ trialStatus })}`}
              data-test-trial-cta-upgrade-link
              data-test-trial-cta-color={trialStatus}
            >
              <Box className={styles.trialCtaButtonContent}>
                <Icon name="arrow-up" />
                <Box overflow="hidden">
                  <Box className={styles.trialCtaButtonTextContent}>Upgrade</Box>
                </Box>
              </Box>
            </Button>
          )}
        </Tooltip>
      ) : null}

      {!isEmptyString(expiryDescription) ? (
        <Box className={styles.trialCtaExpiryDescriptionRow}>
          <InlineStack
            className={styles.trialCtaExpiryDescription}
            alignInline="center"
            alignBlock="center"
            minW="0"
            data-test-trial-cta-expiry
          >
            <Text truncate minW="0" size="xs">
              {expiryDescription}
            </Text>
          </InlineStack>
        </Box>
      ) : null}
    </Stack>
  );
}
